import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { adminService } from '../../services/adminService';

const IntegrationsDashboard: React.FC = () => {
  const [active, setActive] = useState<'slack' | 'email' | 'destinations'>('slack');

  // Slack
  const [slackLoading, setSlackLoading] = useState(true);
  const [slackSaving, setSlackSaving] = useState(false);
  const [slack, setSlack] = useState<{ workspace?: string; channel?: string; enabled?: boolean; event_filters?: string[] }>({});

  useEffect(() => {
    const load = async () => {
      setSlackLoading(true);
      try {
        const cfg = await adminService.getSlackConfig();
        setSlack(cfg || {});
      } catch (e: any) {
        toast.error(e?.message || 'Failed to load Slack config');
      } finally {
        setSlackLoading(false);
      }
    };
    if (active === 'slack') load();
  }, [active]);

  const saveSlack = async () => {
    setSlackSaving(true);
    try {
      await adminService.saveSlackConfig(slack);
      toast.success('Slack config saved');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save Slack config');
    } finally {
      setSlackSaving(false);
    }
  };

  const testSlack = async () => {
    try {
      const res = await adminService.sendSlackTest();
      toast.success(res?.delivered ? 'Test delivered' : 'Test sent');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to send Slack test');
    }
  };

  // Email
  const [emailLoading, setEmailLoading] = useState(true);
  const [emailSaving, setEmailSaving] = useState(false);
  const [email, setEmail] = useState<{ sender?: string; provider?: 'smtp' | 'resend' | 'sendgrid'; enabled?: boolean; templates?: Record<string, string> }>({});

  useEffect(() => {
    const load = async () => {
      setEmailLoading(true);
      try {
        const cfg = await adminService.getEmailConfig();
        setEmail(cfg || {});
      } catch (e: any) {
        toast.error(e?.message || 'Failed to load email config');
      } finally {
        setEmailLoading(false);
      }
    };
    if (active === 'email') load();
  }, [active]);

  const saveEmail = async () => {
    setEmailSaving(true);
    try {
      await adminService.saveEmailConfig(email);
      toast.success('Email config saved');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save email config');
    } finally {
      setEmailSaving(false);
    }
  };

  const testEmail = async () => {
    try {
      const res = await adminService.sendEmailTest();
      toast.success(res?.delivered ? 'Test delivered' : 'Test sent');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to send email test');
    }
  };

  // Destinations
  const [destLoading, setDestLoading] = useState(true);
  const [destinations, setDestinations] = useState<any[]>([]);

  useEffect(() => {
    const load = async () => {
      setDestLoading(true);
      try {
        const list = await adminService.listDestinations();
        setDestinations(Array.isArray(list) ? list : []);
      } catch (e: any) {
        toast.error(e?.message || 'Failed to load destinations');
      } finally {
        setDestLoading(false);
      }
    };
    if (active === 'destinations') load();
  }, [active]);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Integrations</h1>
      <p className="text-sm text-gray-600">Configure Slack and Email notifications, and view available destinations.</p>

      <div className="border-b">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {[
            { id: 'slack', label: 'Slack' },
            { id: 'email', label: 'Email' },
            { id: 'destinations', label: 'Destinations' },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setActive(t.id as any)}
              className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium ${
                active === t.id ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {active === 'slack' && (
        <div className="space-y-4">
          {slackLoading ? (
            <div>Loading...</div>
          ) : (
            <div className="bg-white border rounded p-4 space-y-3 max-w-xl">
              <div>
                <label className="block text-sm font-medium mb-1">Workspace</label>
                <input className="w-full border rounded px-3 py-2" value={slack.workspace || ''} onChange={(e) => setSlack({ ...slack, workspace: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Channel</label>
                <input className="w-full border rounded px-3 py-2" value={slack.channel || ''} onChange={(e) => setSlack({ ...slack, channel: e.target.value })} />
              </div>
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={!!slack.enabled} onChange={(e) => setSlack({ ...slack, enabled: e.target.checked })} />
                <span className="text-sm">Enabled</span>
              </label>
              <div className="flex gap-2">
                <button onClick={saveSlack} disabled={slackSaving} className="px-4 py-2 bg-indigo-600 text-white rounded disabled:opacity-50">{slackSaving ? 'Saving...' : 'Save'}</button>
                <button onClick={testSlack} className="px-4 py-2 border rounded">Send Test</button>
              </div>
            </div>
          )}
        </div>
      )}

      {active === 'email' && (
        <div className="space-y-4">
          {emailLoading ? (
            <div>Loading...</div>
          ) : (
            <div className="bg-white border rounded p-4 space-y-3 max-w-xl">
              <div>
                <label className="block text-sm font-medium mb-1">Sender</label>
                <input className="w-full border rounded px-3 py-2" value={email.sender || ''} onChange={(e) => setEmail({ ...email, sender: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Provider</label>
                <select className="w-full border rounded px-3 py-2" value={email.provider || ''} onChange={(e) => setEmail({ ...email, provider: e.target.value as any })}>
                  <option value="">Select provider</option>
                  <option value="smtp">SMTP</option>
                  <option value="resend">Resend</option>
                  <option value="sendgrid">SendGrid</option>
                </select>
              </div>
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={!!email.enabled} onChange={(e) => setEmail({ ...email, enabled: e.target.checked })} />
                <span className="text-sm">Enabled</span>
              </label>
              <div className="flex gap-2">
                <button onClick={saveEmail} disabled={emailSaving} className="px-4 py-2 bg-indigo-600 text-white rounded disabled:opacity-50">{emailSaving ? 'Saving...' : 'Save'}</button>
                <button onClick={testEmail} className="px-4 py-2 border rounded">Send Test</button>
              </div>
            </div>
          )}
        </div>
      )}

      {active === 'destinations' && (
        <div className="space-y-3">
          {destLoading ? (
            <div>Loading...</div>
          ) : (
            <div className="bg-white border rounded divide-y">
              {destinations.length === 0 && <div className="p-4 text-sm text-gray-500">No destinations available.</div>}
              {destinations.map((d, i) => (
                <div key={i} className="p-4">
                  <div className="font-medium">{d.name || d.id || 'Destination'}</div>
                  <div className="text-xs text-gray-500">{d.type || 'unknown'}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default IntegrationsDashboard;
